/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useLocale } from '@/hooks/useLocale';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { SmartLink as Link } from '@/utils/smartLink';
import { Actionable, Text } from '@az/starc-ui';
import styles from './StoreCardDirectionsLinks.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { countryCodes } from '@/constants/locale';
import { type BottomSheetVariant } from '../../../../interface/types';
import { type YextDisplayCoordinate, type YextStoreEntity } from '../../../../interface/yext-types';
import { getStoreWazeURL } from '@/features/locations/utils/getStoreWazeURL';
import NextImage from '@/components/NextImage';
import {
  CONTACT_COLUMNS_BY_LOCALE,
  getMappedContactColumns,
} from '@/features/locations/utils/getMappedContactColumns';
import starcClassOverrides from '@/theme/starcClassOverrides.module.scss';
import { getStoreLocationUrl } from '@/features/locations/utils/getStoreLocationMapUrl';

export type StoreCardDirectionsLinksProps = {
  isStoreDetailsCard: boolean | undefined;
  storeDetails: YextStoreEntity | undefined;
  openBottomSheet: ((variant: BottomSheetVariant) => void) | undefined;
};

const labelMap = {
  getDirections: 'label_locations_directionsCTA',
  phoneContact: 'label_locations_phoneContact',
  waze: 'label_locations_waze',
  googleMaps: 'label_locations_googleMaps',
  label_locations_contact_column1: 'label_locations_contact_column1',
  label_locations_contact_column2: 'label_locations_contact_column2',
  label_locations_contact_column3: 'label_locations_contact_column3',
};

export function StoreCardDirectionsLinks({
  isStoreDetailsCard,
  openBottomSheet,
  storeDetails,
}: StoreCardDirectionsLinksProps) {
  const locale = useLocale();
  const labels = useLabels(labelMap);

  const useInformationBottomSheet =
    openBottomSheet && (locale === countryCodes.mx || locale === countryCodes.ptBr);

  const directionsUrl = storeDetails?.googlePlaceId
    ? getStoreLocationUrl({
        name: storeDetails.name,
        googlePlaceId: storeDetails.googlePlaceId,
      })
    : null;

  return (
    <FlexGrid
      flexDirection="row"
      justifyContent={isStoreDetailsCard ? 'flex-start' : 'space-between'}
      gap={isStoreDetailsCard ? 8 : 4}
      className={styles.bottomSheetActionsOverrides}
    >
      {!useInformationBottomSheet && (
        <>
          {directionsUrl && (
            <Link to={directionsUrl} target="_blank" rel="noopener noreferrer">
              <Text className={styles.link} decoration="underline">
                {labels.getDirections}
              </Text>
            </Link>
          )}
          {storeDetails?.mainPhone && (
            <Link to={storeDetails.mainPhone} isPhoneNumber>
              <Text className={styles.link} decoration="underline">
                {formatPhoneNumber(storeDetails.mainPhone, locale, { stripCountryCode: true })}
              </Text>
            </Link>
          )}
        </>
      )}
      {useInformationBottomSheet && (
        <>
          <AlternateDirectionsLinks
            googleMapsLink={directionsUrl}
            coordinates={storeDetails?.yextDisplayCoordinate}
            storeDetails={storeDetails}
          />
          {directionsUrl && (
            <Actionable
              attributes={{
                id: 'locations-directions-button',
              }}
              className={styles.mobileActionable}
              onClick={() => {
                openBottomSheet('navigation');
              }}
            >
              <Text className={styles.link} decoration="underline">
                {labels.getDirections}
              </Text>
            </Actionable>
          )}
          {storeDetails?.mainPhone && (
            <Actionable
              attributes={{
                id: 'locations-contacts-button',
              }}
              className={styles.mobileActionable}
              onClick={() => {
                openBottomSheet('contact-methods');
              }}
            >
              <Text className={styles.link} decoration="underline">
                {labels.phoneContact}
              </Text>
            </Actionable>
          )}
        </>
      )}
    </FlexGrid>
  );
}

function AlternateDirectionsLinks({
  googleMapsLink,
  coordinates,
  storeDetails,
}: {
  googleMapsLink: string | null;
  coordinates: YextDisplayCoordinate | undefined;
  storeDetails: YextStoreEntity | undefined;
}) {
  const locale = useLocale();
  const labels = useLabels(labelMap);

  const wazeLink = coordinates ? getStoreWazeURL(coordinates) : null;

  const columns = CONTACT_COLUMNS_BY_LOCALE[locale];
  const mappedColumns = storeDetails
    ? getMappedContactColumns(columns, storeDetails, (phone?: string) =>
        formatPhoneNumber(phone, locale, { stripCountryCode: true }),
      )
    : [];

  return (
    <FlexGrid gap={8} className={styles.desktopLinks}>
      <FlexGrid flexDirection="column" justifyContent="center">
        <FlexGrid gap={8}>
          {wazeLink ? (
            <Link to={wazeLink} target="_blank" rel="noopener noreferrer">
              <FlexGrid as="span" flexDirection="column" gap={1} alignItems="center">
                <NextImage
                  src="https://www.autozone.com/cdn/images/B2C/US/content/Landing/waze.png"
                  width={44}
                  height={44}
                  alt=""
                />
                <Text
                  as="span"
                  variant="text-link"
                  decoration="underline"
                  className={starcClassOverrides['starc-text-link']}
                >
                  {labels.waze}
                </Text>
              </FlexGrid>
            </Link>
          ) : null}

          {googleMapsLink ? (
            <Link to={googleMapsLink} target="_blank" rel="noopener noreferrer">
              <FlexGrid as="span" flexDirection="column" gap={1} alignItems="center">
                <NextImage
                  src="https://www.autozone.com/cdn/images/B2C/US/content/Landing/google-maps.png"
                  width={44}
                  height={44}
                  alt=""
                />
                <Text
                  as="span"
                  variant="text-link"
                  decoration="underline"
                  className={starcClassOverrides['starc-text-link']}
                >
                  {labels.googleMaps}
                </Text>
              </FlexGrid>
            </Link>
          ) : null}
        </FlexGrid>
      </FlexGrid>

      <div className={styles.phoneColumnsGrid}>
        {mappedColumns.map(({ phoneNumbers, column }) => (
          <FlexGrid key={column.labelKey} flexDirection="column" gap={1}>
            <Text
              as="span"
              variant="small-body"
              weight="bold"
              className={starcClassOverrides['starc-small-body']}
            >
              {labels[column.labelKey]}
            </Text>

            {phoneNumbers.map(({ phone, formatted }) => (
              <Link key={phone} to={phone} isPhoneNumber>
                <Text
                  as="span"
                  variant="text-link"
                  decoration="underline"
                  className={starcClassOverrides['starc-text-link']}
                >
                  {formatted}
                </Text>
              </Link>
            ))}
          </FlexGrid>
        ))}
      </div>
    </FlexGrid>
  );
}
